import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  CircularProgress,
  Backdrop,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
} from "@mui/material";
import { Add, Edit, Delete, Settings } from "@mui/icons-material";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import blogImage from "../assets/logo.png";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import DOMPurify from "dompurify";


interface BlogData {
  id?: number;
  slug?: string;
  title: string;
  subheading: string;
  description: string;
  author: string;
  category?: string;
  date?: string;
  image?: string | null;
}

const Blog: React.FC = () => {
  const [blogs, setBlogs] = useState<BlogData[]>([]);
  const [open, setOpen] = useState(false);
  const [editBlog, setEditBlog] = useState<BlogData | null>(null);
  const [form, setForm] = useState<BlogData>({
    title: "",
    subheading: "",
    description: "",
    author: "",
    slug: "",
    category: "",
  });
  const [image, setImage] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [htmlMode, setHtmlMode] = useState(false);
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");


  const quillModules = {
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        ['blockquote', 'code-block'],
        ['link', 'image'],
        ['clean']
      ],
    },
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'blockquote', 'code-block',
    'link', 'image'
  ];




  const API_BASE = "https://api.growl.games/api";
  const token = localStorage.getItem("token") || "";

  const authHeaders = (extra?: Record<string, string>) => ({
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
      ...extra,
    },
  });

  const getAllBlogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/getblog/all`, authHeaders());
      if (res.data?.status === "success" && Array.isArray(res.data.blogs)) {
        setBlogs(res.data.blogs);
      } else {
        setBlogs([]);
      }
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Failed to fetch blogs");
    } finally {
      setLoading(false);
    }
  };

  const createBlog = async (formData: FormData) => {
    const res = await axios.post(
      `${API_BASE}/blogs/createBlog`,
      formData,
      authHeaders({ "Content-Type": "multipart/form-data" })
    );
    return res.data;
  };

  const updateBlog = async (formData: FormData, id: number) => {
    formData.append("id", id.toString());
    await axios.post(
      `${API_BASE}/blogs/updateBlog`,
      formData,
      authHeaders({ "Content-Type": "multipart/form-data" })
    );
  };

  const deleteBlogById = async (id: number) => {
    await axios.post(`${API_BASE}/blogs/deleteBlogById?id=${id}`, {}, authHeaders());
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_BASE}/blogs/categories`, authHeaders());
      if (res.data?.status === "success" && Array.isArray(res.data.categories)) {
        setCategories(res.data.categories);
      }
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    }
  };

  const addCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      const res = await axios.post(
        `${API_BASE}/blogs/categories/add`,
        { name: newCategoryName.trim() },
        authHeaders()
      );
      if (res.data?.status === "success") {
        toast.success("Category added!");
        setNewCategoryName("");
        fetchCategories();
      }
    } catch (err: any) {
      if (err?.response?.status === 409) {
        toast.error("Category already exists");
      } else {
        toast.error("Failed to add category");
      }
    }
  };

  const deleteCategory = async (id: number) => {
    try {
      await axios.post(`${API_BASE}/blogs/categories/delete`, { id }, authHeaders());
      toast.success("Category deleted!");
      fetchCategories();
    } catch (err) {
      toast.error("Failed to delete category");
    }
  };

  useEffect(() => {
    getAllBlogs();
    fetchCategories();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    if (!form.title?.trim() || !form.description?.trim()) {
      toast.error("Title and Description are required.");
      return;
    }

    const formData = new FormData();
    formData.append("title", form.title);
    formData.append("subheading", form.subheading || "");
    formData.append("description", form.description);
    formData.append("author", form.author || "Growl Games Team");
    formData.append("category", form.category || "");
    if (image) formData.append("image", image);
    setSaving(true);

    try {
      if (editBlog?.id) {
        await updateBlog(formData, editBlog.id);
        toast.success("Blog updated successfully!");
        getAllBlogs();
      } else {
        const data = await createBlog(formData);
        if (data?.status === "success" && data?.data?.blog) {
          const newBlog = data.data.blog;
          setBlogs((prev) => [newBlog, ...prev]);
          toast.success("Blog created successfully!");
        } else {
          toast.error(data?.message || "Failed to create blog");
        }
      }
      setOpen(false);
      setEditBlog(null);
      setImage(null);
    } catch (err: any) {
      console.error("Save error:", err);
      toast.error(err?.response?.data?.message || "Failed to save blog");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id?: number) => {
    if (!id) return;
    const ConfirmToast = ({ closeToast }: { closeToast: () => void }) => (
      <div>
        <p>Are you sure you want to delete this blog?</p>
        <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
          <button
            style={{
              background: "#ef4444",
              color: "white",
              border: "none",
              padding: "5px 12px",
              borderRadius: "6px",
              cursor: "pointer",
            }}
            onClick={async () => {
              try {
                await deleteBlogById(id);
                setBlogs((prev) => prev.filter((b) => b.id !== id));
                toast.success("Blog deleted successfully!");
                closeToast();
              } catch (err) {
                toast.error("Failed to delete blog");
              }
            }}
          >
            Confirm
          </button>
          <button
            style={{
              background: "#94a3b8",
              color: "white",
              border: "none",
              padding: "5px 12px",
              borderRadius: "6px",
              cursor: "pointer",
            }}
            onClick={closeToast}
          >
            Cancel
          </button>
        </div>
      </div>
    );

    toast.info(<ConfirmToast closeToast={toast.dismiss} />, {
      autoClose: false,
      closeOnClick: false,
      draggable: false,
      position: "top-center",
    });
  };

  const isFullHtml = (desc: string) =>
    desc?.trimStart().startsWith("<!DOCTYPE") ||
    desc?.trimStart().startsWith("<html") ||
    /<style[\s>]/i.test(desc || "");

  const handleHtmlFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const content = ev.target?.result as string;
      setForm((prev) => ({ ...prev, description: content }));
      setHtmlMode(true);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const openDialog = (blog?: BlogData) => {
    if (blog) {
      setEditBlog(blog);
      const desc = blog.description || "";
      setForm({
        title: blog.title || "",
        subheading: blog.subheading || "",
        description: desc,
        author: blog.author || "",
        slug: blog.slug || "",
        category: blog.category || "",
        id: blog.id,
      });
      setHtmlMode(isFullHtml(desc));
    } else {
      setEditBlog(null);
      setForm({ title: "", subheading: "", description: "", author: "", slug: "", category: "" });
      setHtmlMode(false);
    }
    setImage(null);
    setOpen(true);
  };

  const textTrimmer = (text: string) => {
    if (!text) return "";
    const words = text.trim().split(/\s+/);
    return words.length <= 10 ? text.trim() : words.slice(0, 10).join(" ") + "...";
  };

  return (
    <Box p={3} className="ml-0 lg:ml-[256px] bg-[#0f172a] min-h-screen">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={600} color="white">
          Blog Management
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Settings />}
            onClick={() => setCategoryDialogOpen(true)}
            sx={{ color: "#94a3b8", borderColor: "#94a3b8" }}
          >
            Manage Categories
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => openDialog()}>
            Add Blog
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {blogs.length === 0 && !loading && (
          <Grid item xs={12}>
            <Typography color="#94a3b8">No blogs available.</Typography>
          </Grid>
        )}

        {blogs.map((blog) => {
          const imageUrl = blog.image || blogImage;
          return (
            <Grid item xs={12} sm={6} md={4} key={blog.id}>
              <Card
                sx={{
                  backgroundColor: "#1e293b",
                  color: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                  transition: "0.3s",
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  "&:hover": { transform: "translateY(-4px)" },
                }}
              >
                {/* ✅ Uniform image container */}
                <Box sx={{ width: "100%", height: 200, overflow: "hidden" }}>
                  <img
                    src={`https://api.growl.games/uploads/${imageUrl}`}
                    alt={blog.title}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderTopLeftRadius: "12px",
                      borderTopRightRadius: "12px",
                    }}
                    onError={(e) => (e.currentTarget.src = "/assets/logo.png")}
                  />
                </Box>

                <CardHeader
                  title={<Typography fontWeight={600}>{blog.title}</Typography>}
                  subheader={<Typography color="#9ca3af">{blog.subheading}</Typography>}
                  action={
                    <>
                      <IconButton color="info" onClick={() => openDialog(blog)}>
                        <Edit />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(blog.id)}>
                        <Delete />
                      </IconButton>
                    </>
                  }
                  sx={{
                    background: "linear-gradient(90deg,#0f172a,#1e293b)",
                    color: "#fff",
                    padding: 2,
                  }}
                />

                <Divider />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="body2"
                    color="#e2e8f0"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                      dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(textTrimmer(blog.description)),
                        }}

                  >

                  </Typography>

                  <Box mt={2} display="flex" justifyContent="space-between">
                    <Typography variant="caption" color="#9ca3af">
                      Author: {blog.author}
                    </Typography>
                    <Typography variant="caption" color="#9ca3af">
                      {blog.category || ""}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editBlog ? "Edit Blog" : "Add Blog"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12}>
              <TextField label="Title" name="title" value={form.title} onChange={handleChange} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Subheading" name="subheading" value={form.subheading} onChange={handleChange} fullWidth />
            </Grid>
            {/* <Grid item xs={12}>
              <TextField
                label="Description"
                name="description"
                value={form.description}
                onChange={handleChange}
                fullWidth
                multiline
                minRows={3}
              />
            </Grid> */}

            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2">Description</Typography>
                <Box display="flex" gap={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    component="label"
                    sx={{ fontSize: "0.7rem", py: 0.3, px: 1 }}
                  >
                    Upload HTML File
                    <input type="file" accept=".html,.htm" hidden onChange={handleHtmlFileUpload} />
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setHtmlMode((prev) => !prev)}
                    sx={{ fontSize: "0.7rem", py: 0.3, px: 1 }}
                  >
                    {htmlMode ? "Switch to Editor" : "Switch to HTML"}
                  </Button>
                </Box>
              </Box>
              {htmlMode ? (
                <textarea
                  value={form.description || ""}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={12}
                  placeholder="Paste or type raw HTML here..."
                  style={{
                    width: "100%",
                    padding: "10px",
                    fontFamily: "monospace",
                    fontSize: "0.85rem",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    resize: "vertical",
                    boxSizing: "border-box",
                  }}
                />
              ) : (
                <ReactQuill
                  value={form.description || ""}
                  onChange={(value) => setForm({ ...form, description: value })}
                  modules={quillModules}
                  formats={quillFormats}
                  theme="snow"
                  style={{ background: "#fff" }}
                />
              )}
            </Grid>



            <Grid item xs={12}>
              <TextField label="Author" name="author" value={form.author} onChange={handleChange} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={form.category || ""}
                  label="Category"
                  onChange={(e) => setForm({ ...form, category: e.target.value as string })}
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.name}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" fullWidth>
                {image ? image.name : "Upload Image"}
                <input type="file" accept="image/*" hidden onChange={(e) => setImage(e.target.files?.[0] || null)} />
              </Button>
              {editBlog?.image && !image && (
                <Typography mt={1} variant="caption" color="text.secondary">
                  Current: {editBlog.image.split("/").pop()}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} /> : undefined}
          >
            {saving ? (editBlog ? "Updating..." : "Creating...") : editBlog ? "Save" : "Create Blog"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={categoryDialogOpen}
        onClose={() => setCategoryDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Manage Blog Categories</DialogTitle>
        <DialogContent>
          <Box display="flex" gap={1} mt={1} mb={2}>
            <TextField
              label="New Category"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              fullWidth
              size="small"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addCategory();
                }
              }}
            />
            <Button variant="contained" onClick={addCategory} sx={{ minWidth: 80 }}>
              Add
            </Button>
          </Box>
          <Divider />
          <List>
            {categories.map((cat) => (
              <ListItem key={cat.id} divider>
                <ListItemText primary={cat.name} />
                <ListItemSecondaryAction>
                  <IconButton edge="end" color="error" onClick={() => deleteCategory(cat.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
            {categories.length === 0 && (
              <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                No categories yet
              </Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <ToastContainer position="top-right" theme="colored" />

    </Box>
  );
};

export default Blog;




// import React, { useEffect, useState } from "react";
// import {
//   Box,
//   Grid,
//   Card,
//   CardHeader,
//   CardContent,
//   Typography,
//   Button,
//   IconButton,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   TextField,
//   Divider,
// } from "@mui/material";
// import { Add, Edit, Delete } from "@mui/icons-material";
// import axios from "axios";
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import blogImage from "../assets/logo.png"
// import { CircularProgress, Backdrop } from "@mui/material";


// // Blog Interface
// interface BlogData {
//   id?: number;
//   slug?: string;
//   title: string;
//   subheading: string;
//   description: string;
//   author: string;
//   category?: string;
//   date?: string;
//   image?: string | null;
// }

// // Main Component
// const Blog: React.FC = () => {
//   const [blogs, setBlogs] = useState<BlogData[]>([]);
//   const [open, setOpen] = useState(false);
//   const [editBlog, setEditBlog] = useState<BlogData | null>(null);
//   const [form, setForm] = useState<BlogData>({
//     title: "",
//     subheading: "",
//     description: "",
//     author: "",
//     slug: "",
//     category: "",
//   });
//   const [image, setImage] = useState<File | null>(null);
//   const [loading, setLoading] = useState(false);
//   const [saving, setSaving] = useState(false);

//   const API_BASE = "https://api.growl.games/api";
//   const token = localStorage.getItem("token") || "";

//   // Common Axios Headers
//   const authHeaders = (extra?: Record<string, string>) => ({
//     headers: {
//       Authorization: token ? `Bearer ${token}` : "",
//       ...extra,
//     },
//   });

//   // API FUNCTIONS

//   //  1. Get All Blogs
//   const getAllBlogs = async () => {
//     setLoading(true);
//     try {
//       const res = await axios.get(`${API_BASE}/getblog/all`, authHeaders());
//       if (res.data?.status === "success" && Array.isArray(res.data.blogs)) {
//         setBlogs(res.data.blogs);
//         console.log(res.data.blogs, "res.data.blogs")
//       } else {
//         setBlogs([]);
//       }
//     } catch (err) {
//       console.error("Fetch error:", err);
//       toast.error("Failed to fetch blogs");
//     } finally {
//       setLoading(false);
//     }
//   };

//   // 2. Create Blog
//   const createBlog = async (formData: FormData) => {
//     const res = await axios.post(`${API_BASE}/blogs/createBlog`, formData, authHeaders({ "Content-Type": "multipart/form-data" }));
//     return res.data;
//   };

//   // 3. Update Blog (id in body)
//   const updateBlog = async (formData: FormData, id: number) => {
//     formData.append("id", id.toString());
//     await axios.post(`${API_BASE}/blogs/updateBlog`, formData, authHeaders({ "Content-Type": "multipart/form-data" }));
//   };

//   // 4. Delete Blog (id in query param)
//   const deleteBlogById = async (id: number) => {
//     await axios.post(`${API_BASE}/blogs/deleteBlogById?id=${id}`, {}, authHeaders());
//   };

//   //  Lifecycle
//   useEffect(() => {
//     getAllBlogs();
//   }, []);

//   //  Handle Input
//   const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
//     setForm({ ...form, [e.target.name]: e.target.value });
//   };

//   //  Build Image URL + Fallback
//   const buildImageUrl = (img?: string | null) => {
//     if (!img) return "/assets/no-image.png"; // fallback
//     const trimmed = img.trim();
//     if (trimmed.startsWith("http")) return trimmed;
//     return `https://api.growl.games/${trimmed}`;
//   };

//   // Handle Save
//   const handleSave = async () => {
//     if (!form.title?.trim() || !form.description?.trim()) {
//       toast.error("Title and Description are required.");
//       return;
//     }

//     const formData = new FormData();
//     formData.append("title", form.title);
//     formData.append("subheading", form.subheading || "");
//     formData.append("description", form.description);
//     formData.append("author", form.author || "Growl Games Team");
//     formData.append("category", form.category || "");
//     if (image) formData.append("image", image);
//     setSaving(true);

//     try {
//       if (editBlog?.id) {
//         await updateBlog(formData, editBlog.id);
//         toast.success("Blog updated successfully!");
//         getAllBlogs();

//       } else {
//         const data = await createBlog(formData);
//         if (data?.status === "success" && data?.data?.blog) {
//           const newBlog = data.data.blog;
//           setBlogs((prev) => [newBlog, ...prev]);
//           toast.success("Blog created successfully!");
//         } else {
//           toast.error(data?.message || "Failed to create blog");
//         }
//         console.log("Create response:", data);
//       }
//       setOpen(false);
//       setEditBlog(null);
//       setImage(null);
//     } catch (err: any) {
//       console.error("Save error:", err);
//       toast.error(err?.response?.data?.message || "Failed to save blog");
//     } finally {
//       setSaving(false);
//     }
//   };

//   //  Handle Delete
//   const handleDelete = async (id?: number) => {
//     if (!id) return;
//     const ConfirmToast = ({ closeToast }: { closeToast: () => void }) => (
//       <div >
//         <p>Are you sure you want to delete this blog?</p>
//         <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
//           <button
//             style={{
//               background: "#ef4444",
//               color: "white",
//               border: "none",
//               padding: "5px 12px",
//               borderRadius: "6px",
//               cursor: "pointer",
//             }}
//             onClick={async () => {
//               try {
//                 await deleteBlogById(id);
//                 setBlogs((prev) => prev.filter((b) => b.id !== id));
//                 toast.success("Blog deleted successfully!");
//                 closeToast(); // close confirm toast
//               } catch (err) {
//                 toast.error("Failed to delete blog");
//               }
//             }}
//           >
//             Confirm
//           </button>
//           <button
//             style={{
//               background: "#94a3b8",
//               color: "white",
//               border: "none",
//               padding: "5px 12px",
//               borderRadius: "6px",
//               cursor: "pointer",
//             }}
//             onClick={closeToast}
//           >
//             Cancel
//           </button>
//         </div>
//       </div>
//     );

//     toast.info(<ConfirmToast closeToast={toast.dismiss} />, {
//       autoClose: false, // don’t close automatically
//       closeOnClick: false,
//       draggable: false,
//       position: "top-center",
//     });

//   };

//   // Open Dialog
//   const openDialog = (blog?: BlogData) => {
//     if (blog) {
//       setEditBlog(blog);
//       setForm({
//         title: blog.title || "",
//         subheading: blog.subheading || "",
//         description: blog.description || "",
//         author: blog.author || "",
//         slug: blog.slug || "",
//         category: blog.category || "",
//         id: blog.id,
//       });
//     } else {
//       setEditBlog(null);
//       setForm({ title: "", subheading: "", description: "", author: "", slug: "", category: "" });
//     }
//     setImage(null);
//     setOpen(true);
//   };

//   const textTrimmer = (text: string) => {
//   if (!text) return "";

//   const words = text.trim().split(/\s+/); // Split by any whitespace
//   return words.length <= 30
//     ? text.trim()
//     : words.slice(0, 3).join(" ") + "...";
// }


//   // 🔹 JSX UI

//   return (
//     <Box p={3} className="ml-0 lg:ml-[256px] bg-[#0f172a] min-h-screen">
//       {/* Header */}
//       <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
//         <Typography variant="h5" fontWeight={600} color="white">
//           Blog Management
//         </Typography>
//         <Button variant="contained" startIcon={<Add />} onClick={() => openDialog()}>
//           Add Blog
//         </Button>
//       </Box>

//       {/* Blog List */}
//       <Grid container spacing={3}>
//         {blogs.length === 0 && !loading && (
//           <Grid item xs={12}>
//             <Typography color="#94a3b8">No blogs available.</Typography>
//           </Grid>
//         )}

//         {blogs.map((blog) => {
//           // const imageUrl = buildImageUrl(blog.image);
//           const imageUrl = blog.image || blogImage;

//           return (
//             <Grid item xs={12} sm={6} md={4} key={blog.id}>
//               <Card
//                 sx={{
//                   backgroundColor: "#1e293b",
//                   color: "#fff",
//                   borderRadius: 3,
//                   boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
//                   transition: "0.3s",
//                   "&:hover": { transform: "translateY(-4px)" },
//                 }}
//               >
//                 <div style={{width: "100%", height: "", overflow: "hidden" }}>
//                 <img
//                   src={`https://api.growl.games/uploads/${imageUrl}`}
//                   alt={blog.title}
//                   style={{
//                     width: "100%",
//                     objectFit: "cover",
//                     borderTopLeftRadius: "12px",
//                     borderTopRightRadius: "12px",
//                   }}
//                   onError={(e) => (e.currentTarget.src = "/assets/logo.png")}
//                 />

//                 </div>

//                 <CardHeader
//                   title={<Typography fontWeight={600}>{blog.title}</Typography>}
//                   subheader={<Typography color="#9ca3af">{blog.subheading}</Typography>}
//                   action={
//                     <>
//                       <IconButton color="info" onClick={() => openDialog(blog)}>
//                         <Edit />
//                       </IconButton>
//                       <IconButton color="error" onClick={() => handleDelete(blog.id)}>
//                         <Delete />
//                       </IconButton>
//                     </>
//                   }
//                   sx={{
//                     background: "linear-gradient(90deg,#0f172a,#1e293b)",
//                     color: "#fff",
//                     padding: 2,
//                   }}
//                 />
//                 <Divider />
//                 <CardContent>
//                   <Typography variant="body2" color="#e2e8f0">
//                     {textTrimmer(blog.description)}
//                   </Typography>
//                   <Box mt={2} display="flex" justifyContent="space-between">
//                     <Typography variant="caption" color="#9ca3af">
//                       Author: {blog.author}
//                     </Typography>
//                     <Typography variant="caption" color="#9ca3af">
//                       {blog.category || ""}
//                     </Typography>
//                   </Box>
//                 </CardContent>
//               </Card>
//             </Grid>
//           );
//         })}
//       </Grid>

//       {/* Dialog */}
//       <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
//         <DialogTitle>{editBlog ? "Edit Blog" : "Add Blog"}</DialogTitle>
//         <DialogContent>
//           <Grid container spacing={2} mt={1}>
//             <Grid item xs={12}>
//               <TextField label="Title" name="title" value={form.title} onChange={handleChange} fullWidth />
//             </Grid>

//             <Grid item xs={12}>
//               <TextField label="Subheading" name="subheading" value={form.subheading} onChange={handleChange} fullWidth />
//             </Grid>

//             <Grid item xs={12}>
//               <TextField
//                 label="Description"
//                 name="description"
//                 value={form.description}
//                 onChange={handleChange}
//                 fullWidth
//                 multiline
//                 minRows={3}
//               />
//             </Grid>

//             <Grid item xs={12}>
//               <TextField label="Author" name="author" value={form.author} onChange={handleChange} fullWidth />
//             </Grid>

//             <Grid item xs={12}>
//               <TextField label="Category" name="category" value={form.category} onChange={handleChange} fullWidth />
//             </Grid>

//             <Grid item xs={12}>
//               <Button variant="outlined" component="label" fullWidth>
//                 {image ? image.name : "Upload Image"}
//                 <input type="file" accept="image/*" hidden onChange={(e) => setImage(e.target.files?.[0] || null)} />
//               </Button>
//               {editBlog?.image && !image && (
//                 <Typography mt={1} variant="caption" color="text.secondary">
//                   Current: {editBlog.image.split("/").pop()}
//                 </Typography>
//               )}
//             </Grid>
//           </Grid>
//         </DialogContent>

//         <DialogActions>
//           <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
//           <Button
//             variant="contained"
//             onClick={handleSave}
//             disabled={saving}
//             startIcon={saving ? <CircularProgress size={18} /> : undefined}
//           >
//             {saving ? (editBlog ? "Updating..." : "Creating...") : (editBlog ? "Save" : "Create Blog")}
//           </Button>
//         </DialogActions>
//       </Dialog>

//       <ToastContainer position="top-right" theme="colored" />
//     </Box>
//   );
// };

// export default Blog;
